import { useMemo, useState } from 'react';
import type { TabProps } from './Tab';

type UseTabs<T extends string> = {
  tabs: Array<Pick<TabProps, 'isActive' | 'onSelect'> & { label: T }>;
  activeTab: T;
};

/**
 * You provide a list of tab IDs, this hook will give you back an
 * array which contains data for the tabs, managing which tab is active.
 *
 * Does not include an icon or badge count, as those are optional,
 * however you could can manually inject them or create a new array based on this.
 *
 * @example
 * const MyTabs = () => {
 *  const tabs = useTabs('Foo', 'Bar', 'Baz');
 *  return (
 *   <Tabs>
 *     {tabs.map((tab) => (
 *       <Tabs.Tab
 *         key={tab.label}
 *         {...tab}
 *       />
 *     ))}
 *   </Tabs>
 * };
 */
const useTabs = <T extends string>({
  tabIds,
  defaultTab,
}: {
  tabIds: T[];
  defaultTab?: T;
}): UseTabs<T> => {
  const [activeTab, setActiveTab] = useState<T>(defaultTab || tabIds[0] as T);
  const tabs = useMemo(() => {
    return tabIds.map((tabId) => ({
      label: tabId,
      isActive: tabId === activeTab,
      onSelect: () => setActiveTab(tabId),
    }));
  }, [tabIds, activeTab, setActiveTab]);
  return {
    tabs,
    activeTab,
  };
};

export default useTabs;
